import { useCallback, useEffect, useReducer, useRef } from 'react';
import type { AgentClient } from './agentClient';
import { agentsTabReducer, initialAgentsTabState } from './agentsTabReducer';
import type { MemoryItem } from './types';

/** Loads the catalog and the caller's own memory lists; a null client means "not configured". */
export function useAgentsTab(client: AgentClient | null) {
  const [state, dispatch] = useReducer(
    agentsTabReducer,
    undefined,
    initialAgentsTabState,
  );
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);
  const generation = useRef(0);

  const load = useCallback(() => {
    if (!client) return () => undefined;
    const run = ++generation.current;
    const controller = new AbortController();
    const live = () => run === generation.current && !controller.signal.aborted;
    dispatch({ type: 'load' });
    client
      .listAgents(controller.signal)
      .then((catalog) => {
        if (!live()) return;
        dispatch({ type: 'loaded', catalog });
        catalog.agents
          .filter((a) => a.memory.preferences)
          .forEach((a) => {
            client
              .listMemory(a.name, controller.signal)
              .then((list) => {
                if (live())
                  dispatch({
                    type: 'memory_loaded',
                    agent: a.name,
                    items: list.items,
                  });
              })
              .catch((error) => {
                if (live())
                  dispatch({ type: 'memory_failed', agent: a.name, error });
              });
          });
      })
      .catch((error) => {
        if (live()) dispatch({ type: 'load_failed', error });
      });
    return () => controller.abort();
  }, [client]);

  useEffect(() => load(), [load]);

  const remove = useCallback(
    async (agent: string, item: MemoryItem) => {
      if (!client) return;
      const items = stateRef.current.memory[agent]?.items ?? [];
      const index = Math.max(
        0,
        items.findIndex((i) => i.id === item.id),
      );
      dispatch({ type: 'delete', agent, id: item.id });
      try {
        await client.deleteMemory(agent, item.id);
      } catch (error) {
        dispatch({ type: 'delete_failed', agent, item, index, error });
      }
    },
    [client],
  );

  return { state, remove, refresh: load, configured: client !== null };
}
